import { Box, Button, Flex } from 'rebass';
import { FaCheck } from 'react-icons/fa';
import { useWindowSize } from 'the-platform';
import AvatarEditor from 'react-avatar-editor';
import PropTypes from 'prop-types';
import React, { useState, useMemo, useRef, useEffect } from 'react';

const CoverEditor = ({ src, acceptEdit, exitEditor, editorWidth, initialZoom = 1, px = 0 }) => {
  const [zoom, setZoom] = useState(initialZoom);
  const { width: windowWidth } = useWindowSize();
  const editor = useRef(null);
  const boxRef = useRef(null);
  const [localSrc] = useState(src);

  const handleEdit = async () => {
    const canvas = editor.current.getImage();
    const file = await new Promise(resolve => {
      canvas.toBlob(
        blob => {
          const newImage = blob;
          newImage.name = 'cover-image.png';
          resolve(newImage);
        },
        'image/png',
        100
      );
    });

    acceptEdit(file);
  };

  const handleZoom = e => setZoom(Number(e.target.value));

  const width = useMemo(() => {
    if (editorWidth) return editorWidth;
    return Math.min(windowWidth - (windowWidth < 500 ? 80 : 16), 600);
  }, [editorWidth, windowWidth]);

  // ── Mobile only: pinch-to-zoom (drag-to-pan is already handled natively by
  // ReactAvatarEditor). Desktop keeps the zoom slider. Same mechanism as
  // collections/book-creation/components/Editor.js.
  const isTouchDevice =
    typeof window !== 'undefined' &&
    ('ontouchstart' in window || (navigator.maxTouchPoints || 0) > 0);
  const useTouchGestures = windowWidth < 500 && isTouchDevice;
  const zoomMin = 1;
  const zoomMax = 2;

  // Keep the latest zoom readable inside the (once-attached) native listeners.
  const zoomRef = useRef(zoom);
  useEffect(() => { zoomRef.current = zoom; }, [zoom]);
  const pinchRef = useRef(null);
  const gestureActiveRef = useRef(false);

  useEffect(() => {
    const el = boxRef.current;
    if (!el || !useTouchGestures) return undefined;

    const applyZoom = (raw) => {
      const clamped = Math.min(zoomMax, Math.max(zoomMin, raw));
      setZoom(Number(clamped.toFixed(2)));
    };
    const distance = (touches) =>
      Math.hypot(
        touches[0].clientX - touches[1].clientX,
        touches[0].clientY - touches[1].clientY,
      );

    // iOS Safari AND iOS Chrome (both WebKit) implement pinch via non-standard
    // GestureEvents and usually withhold the 2-finger touchmove sequence, so the
    // touch-distance path below never fires there — these handle those browsers.
    const onGestureStart = (e) => {
      e.preventDefault();
      gestureActiveRef.current = true;
      pinchRef.current = { startZoom: Number(zoomRef.current) || zoomMin };
    };
    const onGestureChange = (e) => {
      e.preventDefault();
      if (pinchRef.current) applyZoom(pinchRef.current.startZoom * e.scale);
    };
    const onGestureEnd = (e) => {
      e.preventDefault();
      gestureActiveRef.current = false;
      pinchRef.current = null;
    };

    // Touch-distance pinch for non-WebKit touch browsers (e.g. Android Chrome).
    const onTouchStart = (e) => {
      if (e.touches.length === 2 && !gestureActiveRef.current) {
        pinchRef.current = {
          startDist: distance(e.touches) || 1,
          startZoom: Number(zoomRef.current) || zoomMin,
        };
      }
    };
    const onTouchMove = (e) => {
      if (gestureActiveRef.current) return; // WebKit gesture handlers own it
      if (e.touches.length === 2 && pinchRef.current && pinchRef.current.startDist) {
        // We drive zoom from the pinch; stop the browser's native page zoom.
        // (React's onTouchMove is passive, so this must be a native listener.)
        e.preventDefault();
        const ratio = distance(e.touches) / pinchRef.current.startDist;
        applyZoom(pinchRef.current.startZoom * ratio);
      }
    };
    const endPinch = (e) => {
      if (e.touches.length < 2) pinchRef.current = null;
    };

    el.addEventListener('gesturestart', onGestureStart, { passive: false });
    el.addEventListener('gesturechange', onGestureChange, { passive: false });
    el.addEventListener('gestureend', onGestureEnd, { passive: false });
    el.addEventListener('touchstart', onTouchStart, { passive: false });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', endPinch, { passive: false });
    el.addEventListener('touchcancel', endPinch, { passive: false });
    return () => {
      el.removeEventListener('gesturestart', onGestureStart);
      el.removeEventListener('gesturechange', onGestureChange);
      el.removeEventListener('gestureend', onGestureEnd);
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', endPinch);
      el.removeEventListener('touchcancel', endPinch);
    };
  }, [useTouchGestures, zoomMin, zoomMax]);

  return (
    <Box>
      <Box ref={boxRef} sx={useTouchGestures ? { touchAction: 'none' } : undefined}>
        <AvatarEditor
          image={localSrc}
          scale={zoom}
          ref={editor}
          width={width - 40}
          height={width / 2 - 20}
          border={20}
          onMouseUp={handleEdit}
        />
      </Box>
      <Flex px={px} alignItems="center" justifyContent="center">
        {/* Mobile uses pinch-to-zoom instead of the slider; desktop keeps it. */}
        {!useTouchGestures && <input
          type="range"
          min="1"
          max="2"
          step="0.01"
          value={zoom}
          onChange={handleZoom}
          onMouseUp={handleEdit}
        />}
        <Button
          variant="darkIconCircle"
          type="button"
          ml="20px"
          justifyContent="center"
          alignItems="center"
          width={30}
          height={30}
          fontSize={18}
          onClick={() => {
            handleEdit();
            exitEditor();
          }}
        >
          <FaCheck />
        </Button>
      </Flex>
    </Box>
  );
};

CoverEditor.propTypes = {
  src: PropTypes.string.isRequired,
  acceptEdit: PropTypes.func.isRequired,
  exitEditor: PropTypes.func.isRequired,
  editorWidth: PropTypes.number,
};

CoverEditor.defaultProps = {
  editorWidth: null,
};

export default CoverEditor;
